import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronLeft, ChevronRight, BookOpen, Globe, Award, Music, Users, 
  Calendar, MapPin, Star, Heart, GraduationCap, Crown,
  ArrowUp, Drum, Trophy
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';


const MestreFullStoryInner = () => {
  const hero = useHero('mestre-full-story');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Refs for each section
  const introRef = useRef(null);
  const achievementsRef = useRef(null);
  const casaSambaRef = useRef(null);
  const capoeiraRef = useRef(null);
  const programsRef = useRef(null);

  const sections = [
    { id: 'intro', label: 'Introduction', ref: introRef },
    { id: 'achievements', label: 'Achievements', ref: achievementsRef },
    { id: 'casasamba', label: 'Casa Samba', ref: casaSambaRef },
    { id: 'capoeira', label: 'Capoeira', ref: capoeiraRef },
    { id: 'programs', label: 'Programs & Reach', ref: programsRef },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Navigation component for sections
  const SectionNav = ({ currentIndex }) => {
    const prevSection = currentIndex > 0 ? sections[currentIndex - 1] : null;
    const nextSection = currentIndex < sections.length - 1 ? sections[currentIndex + 1] : null;

    return (
      <div className="flex justify-between items-center mt-12 pt-8 border-t border-stone-800">
        {prevSection ? (
          <button
            onClick={() => scrollToSection(prevSection.ref)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
          >
            <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Previous: <span className="text-amber-400">{prevSection.label}</span></span>
          </button>
        ) : (
          <div />
        )}
        
        {nextSection ? (
          <button
            onClick={() => scrollToSection(nextSection.ref)}
            className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors group"
          >
            <span>Next: <span className="font-bold">{nextSection.label}</span></span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        ) : (
          <Link
            to="/legacy"
            className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors group"
          >
            <span>Back to Legacy Page</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-stone-950">
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-stone-900 to-stone-950 border-b border-amber-500/20">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Link 
            to="/legacy" 
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors mb-8"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Legacy
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
                <Crown className="w-8 h-8 text-black" />
              </div>
              <div>
                <span className="text-amber-500 font-bold tracking-[0.2em] text-sm block">
                  <PageEditable path="hero.eyebrow" fallback={hero.kicker || "THE COMPLETE STORY"} />
                </span>
                <h1 className="text-4xl md:text-5xl font-black text-white">
                  <PageEditable path="hero.title" fallback={hero.title_top || "Mestre Curtis Pierre"} />
                </h1>
                <p className="text-xl text-amber-400 font-semibold mt-1">
                  <PageEditable path="hero.subtitle" fallback='"The Sambman"' />
                </p>
              </div>
            </div>
            
            {/* Quick Jump Navigation */}
            <div className="mt-8 flex flex-wrap gap-2">
              <span className="text-gray-500 text-sm py-2">Jump to:</span>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.ref)}
                  className="px-4 py-2 text-sm bg-stone-800 hover:bg-stone-700 text-gray-300 hover:text-white rounded-lg transition-colors"
                >
                  {section.label}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="bg-stone-900/50 border-b border-amber-500/10">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-black text-amber-500">45+</div>
              <div className="text-sm text-gray-500">Years Dedicated</div>
            </div>
            <div>
              <div className="text-3xl font-black text-amber-500">30+</div>
              <div className="text-sm text-gray-500">Years Teaching</div>
            </div>
            <div>
              <div className="text-3xl font-black text-amber-500">2</div>
              <div className="text-sm text-gray-500">Brasilian Masteries</div>
            </div>
            <div>
              <div className="text-3xl font-black text-amber-500">1987</div>
              <div className="text-sm text-gray-500">Founded Escola de Samba Casa Samba</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        
        {/* Section 1: Introduction */}
        <motion.section
          ref={introRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 scroll-mt-24"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Crown className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <span className="text-amber-500 font-semibold tracking-wider text-sm">SECTION 1 OF {sections.length}</span>
              <h2 className="text-3xl font-black text-white">
                <PageEditable path="s1.heading" as="span" fallback="Introduction" />
              </h2>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-500/10 to-transparent border-l-4 border-amber-500 px-6 py-4 rounded-r-xl mb-8">
            <p className="text-amber-400 font-medium italic text-lg">
              <PageEditable path="s1.pullquote" as="span" multiline fallback="&quot;Mestre&quot; meaning – Master Teacher/Cultural Guardian, in this case, we're speaking of Brasilian culture." />
            </p>
          </div>

          <div className="prose prose-lg prose-invert max-w-none space-y-6">
            <p className="text-gray-300 leading-relaxed text-lg">
              <PageEditable path="s1.para1" as="span" multiline fallback="Mr. Curtis has a title that should be mentioned before his name: Mestre meaning – Master Teacher/Cultural Guardian, in this case, we're speaking of Brasilian culture. This achievement cannot be obtained at any university in the world." />
            </p>
            <p className="text-gray-300 leading-relaxed text-lg">
              <PageEditable path="s1.para2" as="span" multiline fallback="As a matter of fact, Mestre Curtis has provided help or has been instrumental in providing information about Brasilian culture in New Orleans as it relates to Carnival in New Orleans, as well as how the Afro Brasilian martial art Capoeira is related to our Mardi Gras Indian culture and the second-line dancers of today." />
            </p>
          </div>

          <SectionNav currentIndex={0} />
        </motion.section>

        {/* Section 2: Achievements */}
        <motion.section
          ref={achievementsRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 pt-16 scroll-mt-24"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm">SECTION 2 OF {sections.length}</span>
              <h2 className="text-3xl font-black text-white">Historic Achievements</h2>
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-500/10 to-transparent border-l-4 border-yellow-500 px-6 py-4 rounded-r-xl mb-8">
            <p className="text-yellow-400 font-medium italic text-lg">
              "The only American who has reached a Master level in 2 Brasilian cultures"
            </p>
          </div>

          <div className="prose prose-lg prose-invert max-w-none space-y-6">
            <p className="text-gray-300 leading-relaxed text-lg">
              <strong className="text-amber-400">Mestre Curtis</strong> is classified as the <strong className="text-white">only American</strong> who has reached a Master level in <strong className="text-white">2 Brasilian cultures</strong> meaning that he is the first person in this country to be recognized as:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 my-8">
              <div className="p-6 rounded-xl bg-gradient-to-r from-amber-500/20 to-yellow-500/10 border border-amber-500/50">
                <h4 className="font-bold text-lg mb-2 text-amber-400">Master of Capoeira</h4>
                <p className="text-gray-400">The first original African martial art to leave Africa</p>
              </div>
              <div className="p-6 rounded-xl bg-gradient-to-r from-amber-500/20 to-yellow-500/10 border border-amber-500/50">
                <h4 className="font-bold text-lg mb-2 text-amber-400">Master of Brasilian Percussion</h4>
                <p className="text-gray-400">And the first to bring Master Brasilian teachers to New Orleans in the early '90s</p>
              </div>
            </div>

            <p className="text-gray-300 leading-relaxed text-lg">
              This is known by only a handful of people in the United States. <strong className="text-amber-400">Mestre Curtis</strong> has dedicated his life to his African roots of culture which he found in <strong className="text-white">Brasil</strong> after 45 years ago.
            </p>
            <p className="text-gray-300 leading-relaxed text-lg">
              <strong className="text-amber-400">Mestre Curtis</strong> has been on this dedicated path which he never changes – its tradition and commitment to presenting authentic and traditional Afro Brasilian culture to his community, the city, the state, and in this country. Our very own citizens and great musicians of this community support him and the beautiful programming work that he does with community children and adults.
            </p>
            <p className="text-gray-300 leading-relaxed text-lg">
              <strong className="text-amber-400">Mestre Curtis</strong> has been relentlessly on his commitment for the past <strong className="text-white">45 years</strong>. He is directly involving himself with the community and the city. As a result, <strong className="text-amber-400">Mestre Curtis</strong> has piloted and started countless youth programs.
            </p>
            <p className="text-gray-300 leading-relaxed text-lg">
              His programs for children have been fully funded directly by him and his group <strong className="text-amber-400">Escola de Samba Casa Samba</strong>.
            </p>
            <p className="text-gray-300 leading-relaxed text-lg">
              He has also published his first book <strong className="text-white">"Capoeira Angola Movements"</strong> on the Afro Brasilian Martial Art Capoeira. He has also been fortunate enough to teach classes in <strong className="text-white">Brasil</strong> on both Samba and Capoeira.
            </p>
          </div>

          <SectionNav currentIndex={1} />
        </motion.section>

        {/* Section 3: Casa Samba */}
        <motion.section
          ref={casaSambaRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 pt-16 scroll-mt-24"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <Music className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <span className="text-green-500 font-semibold tracking-wider text-sm">SECTION 3 OF {sections.length}</span>
              <h2 className="text-3xl font-black text-white">Escola de Samba Casa Samba</h2>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500/10 to-transparent border-l-4 border-green-500 px-6 py-4 rounded-r-xl mb-8">
            <p className="text-green-400 font-medium italic text-lg">
              "The first samba school in New Orleans, one of the premier cities of Carnival in the world."
            </p>
          </div>

          <div className="prose prose-lg prose-invert max-w-none space-y-6">
            <p className="text-gray-300 leading-relaxed text-lg">
              <strong className="text-amber-400">Escola de Samba Casa Samba</strong> was founded in <strong className="text-white">1987</strong> at <strong className="text-white">Tulane University</strong> by Mestre Curtis Pierre, Bill Lennon, Chuk and Carolyn Barber.
            </p>
            <p className="text-gray-300 leading-relaxed text-lg">
              On <strong className="text-white">July 21, 1993</strong>, Mayor Sidney Bartholomew proclaimed <strong className="text-amber-400">Escola de Samba Casa Samba</strong> as the <strong className="text-white">first samba school in New Orleans</strong>, one of the premier cities of Carnival in the world.
            </p>
            <p className="text-gray-300 leading-relaxed text-lg">
              The group is directed by <strong className="text-amber-400">Mestre Curtis Pierre</strong>, and Brasilian master drummer, <strong className="text-white">Mestre Jorge "Alabe" Bezerra</strong>.
            </p>
          </div>

          <SectionNav currentIndex={2} />
        </motion.section>

        {/* Section 4: Capoeira */}
        <motion.section
          ref={capoeiraRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 pt-16 scroll-mt-24"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <span className="text-red-500 font-semibold tracking-wider text-sm">SECTION 4 OF {sections.length}</span>
              <h2 className="text-3xl font-black text-white">Capoeira in New Orleans</h2>
            </div>
          </div>

          <div className="prose prose-lg prose-invert max-w-none space-y-6">
            <p className="text-gray-300 leading-relaxed text-lg">
              <strong className="text-amber-400">Mestre Curtis Pierre</strong> was the <strong className="text-white">first person to bring Capoeira to New Orleans</strong>. The Afro Brasilian martial art <strong className="text-white">Capoeira Angola</strong> has maintained classes for over <strong className="text-white">30 years</strong> to present.
            </p>
            <p className="text-gray-300 leading-relaxed text-lg">
              He was taught by <strong className="text-white">Mestre Cobrinha Mansa</strong> of Salvador Bahia.
            </p>
            <p className="text-gray-300 leading-relaxed text-lg">
              <strong className="text-amber-400">Mestre Curtis</strong> published his first book on Capoeira Angola in <strong className="text-white">2017</strong>: <strong className="text-amber-400">"Capoeira Angola Movements"</strong>
            </p>
          </div>

          <SectionNav currentIndex={3} />
        </motion.section>

        {/* Section 5: Programs & Reach */}
        <motion.section
          ref={programsRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-8 pt-16 scroll-mt-24"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Globe className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <span className="text-blue-500 font-semibold tracking-wider text-sm">SECTION 5 OF {sections.length}</span>
              <h2 className="text-3xl font-black text-white">Programs & International Reach</h2>
            </div>
          </div>

          <div className="prose prose-lg prose-invert max-w-none space-y-6">
            <p className="text-gray-300 leading-relaxed text-lg">
              Since <strong className="text-white">1998</strong>, <strong className="text-amber-400">Mestre Curtis Pierre "The Samba Man"</strong> has conducted his interactive Brasilian drumming programs at:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6">
              {["Schools", "Museums", "Performing Arts Centers", "Workshops", "Conventions", "Festivals", "Team-building Events"].map((venue, index) => (
                <div key={index} className="p-3 bg-stone-900/50 border border-stone-800 rounded-lg text-center text-gray-300">
                  {venue}
                </div>
              ))}
            </div>

            <h3 className="text-xl font-bold text-white mt-8 mb-4">Locations Across the United States:</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6">
              {["California", "Oregon", "Washington", "Washington D.C.", "New York", "Pennsylvania", "Louisiana", "Mississippi", "Texas", "Virginia", "Arizona"].map((state, index) => (
                <div key={index} className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center text-blue-300">
                  {state}
                </div>
              ))}
            </div>

            <h3 className="text-xl font-bold text-white mt-8 mb-4">International:</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6">
              {["Brasil", "Mexico", "Japan", "Korea"].map((country, index) => (
                <div key={index} className="p-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/10 border border-amber-500/50 rounded-lg text-center text-amber-400 font-medium">
                  {country}
                </div>
              ))}
            </div>
          </div>

          <SectionNav currentIndex={4} />
        </motion.section>
      </div>

      {/* Bottom CTA */}
      <div className="bg-gradient-to-t from-amber-500/10 to-transparent py-16 border-t border-stone-800 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Continue the Journey</h3>
          <p className="text-gray-400 mb-8">
            The Master's story continues in the roda. Take a class, keep the lineage alive.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/kapoeira-angola"
              className="px-6 py-3 bg-amber-500 text-black font-bold rounded-xl hover:bg-amber-400 transition-colors inline-flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              Kapoeira Classes
            </Link>
            <Link
              to="/mestre-bullfrog"
              className="px-6 py-3 border border-green-500/50 text-green-400 font-bold rounded-xl hover:bg-green-500/10 transition-colors"
            >
              The Frog Style
            </Link>
            <Link
              to="/classes"
              className="px-6 py-3 border border-white/30 text-white font-bold rounded-xl hover:bg-white/10 transition-colors"
            >
              All In-Person Classes
            </Link>
            <Link
              to="/legacy"
              className="px-6 py-3 border border-white/20 text-gray-300 font-bold rounded-xl hover:bg-white/10 transition-colors"
            >
              Back to Legacy
            </Link>
          </div>
          
          <button
            onClick={scrollToTop}
            className="mt-8 inline-flex items-center gap-2 text-gray-500 hover:text-white transition-colors"
          >
            <ArrowUp className="w-4 h-4" />
            Back to Top
          </button>
        </div>
      </div>

      {/* Floating Scroll to Top Button */}
      {showScrollTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 w-12 h-12 bg-amber-500 text-black rounded-full shadow-lg shadow-amber-500/30 flex items-center justify-center hover:bg-amber-400 transition-colors z-50"
        >
          <ArrowUp className="w-6 h-6" />
        </motion.button>
      )}
    </div>
  );
};


import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const MestreFullStory = () => (
  <>
    <MestreFullStoryInner />
    <AdminEditorLink to="/admin/about-editor" label="Edit Mestre's Story" />
  </>
);

export default MestreFullStory;
